import { type File, useStores } from '@littlebird/sdk';
import { observer } from 'mobx-react-lite';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { Icons } from '@/assets/icons/icons';
import { Button } from '@/components/ui';
import { XStack, YStack } from '@/components/ui/layout/layout';
import { SegmentedControl } from '@/components/ui/segmented-control';
import { Skeleton } from '@/components/ui/skeleton';
import { Markdown } from '@/components/ui/smoothdown/Markdown';
import { CanvasFrame } from '@/features/canvas';
import { SpriteFrame } from '@/features/sprites/SpriteFrame';
import { cn } from '@/lib/utils';
import { userStore } from '@/stores/user.store';
import { downloadFileWithFeedback } from '@/utils/file-io';
import {
  getDisplayFilename,
  getFileExtension,
  isJsonFile,
} from '@/utils/files';

import { getFileVersionKey, isOfficeRenderer } from './artifact-presentation';
import { OfficeFrame, XlsxPreview } from './office';
import { PdfPreviewFrame } from './PdfPreviewFrame';
import { useArtifactContent } from './useArtifactContent';

export type HtmlPreviewMode = 'preview' | 'code';

export const HtmlPreviewModeToggle = ({
  mode,
  onModeChange,
}: {
  mode: HtmlPreviewMode;
  onModeChange: (mode: HtmlPreviewMode) => void;
}) => (
  <SegmentedControl.Root
    value={mode}
    onValueChange={(value) => onModeChange(value as HtmlPreviewMode)}
  >
    <SegmentedControl.List className="h-7 rounded-md">
      <SegmentedControl.Trigger value="preview" className="h-full px-2 text-xs">
        Preview
      </SegmentedControl.Trigger>
      <SegmentedControl.Trigger value="code" className="h-full px-2 text-xs">
        Code
      </SegmentedControl.Trigger>
    </SegmentedControl.List>
  </SegmentedControl.Root>
);

const DownloadOnlyPanel = ({
  message,
  onDownload,
}: {
  message: string;
  onDownload: () => void;
}) => (
  <YStack className="h-full items-center justify-center gap-3 p-6 text-center">
    <Icons.file className="size-8 text-foreground-subtle" />
    <span className="text-sm text-foreground-muted">{message}</span>
    <Button variant="outline" size="sm" onClick={onDownload}>
      <XStack className="items-center gap-2">
        <Icons.download className="size-4" />
        Download
      </XStack>
    </Button>
  </YStack>
);

const PlainTextView = ({
  text,
  className,
}: {
  text: string;
  className?: string;
}) => (
  <div
    className={cn(
      'h-full overflow-auto p-3 text-xs font-mono whitespace-pre-wrap break-words bg-surface select-text cursor-text',
      className
    )}
  >
    {text}
  </div>
);

const prettyPrintJson = (text: string) => {
  try {
    return JSON.stringify(JSON.parse(text), null, 2);
  } catch {
    return text;
  }
};

interface FilePreviewContentProps {
  file: File;
  chatId?: string;
  htmlViewMode?: HtmlPreviewMode;
  canvasDocumentMode?: boolean;
  onCanvasDocumentReadyChange?: (isReady: boolean) => void;
}

function FilePreviewContentVersion({
  file,
  chatId,
  htmlViewMode = 'preview',
  canvasDocumentMode = false,
  onCanvasDocumentReadyChange,
}: FilePreviewContentProps) {
  const { files: filesStore } = useStores();
  const displayName = getDisplayFilename(file.filename);
  const { renderer, status, text, bytes, url, canvas, versionKey } =
    useArtifactContent(file, {
      surface: 'preview',
      enabled: true,
    });
  const [officeRenderFailed, setOfficeRenderFailed] = useState(false);
  const handleOfficeRenderError = useCallback(
    () => setOfficeRenderFailed(true),
    []
  );
  const plainText = useMemo(
    () =>
      isJsonFile(file.contentType, file.filename)
        ? prettyPrintJson(text)
        : text,
    [file.contentType, file.filename, text]
  );

  const download = () =>
    void downloadFileWithFeedback(filesStore, file, 'FilePreviewContent');

  useEffect(() => {
    if (
      canvasDocumentMode &&
      renderer === 'canvas' &&
      (status === 'error' || status === 'too_large')
    ) {
      onCanvasDocumentReadyChange?.(true);
    }
  }, [canvasDocumentMode, onCanvasDocumentReadyChange, renderer, status]);

  const extension = getFileExtension(file.filename);
  if (renderer === 'none' || officeRenderFailed) {
    return (
      <DownloadOnlyPanel
        message={`No preview available for ${extension} files.`}
        onDownload={download}
      />
    );
  }

  if (status === 'idle' || status === 'loading') {
    return <Skeleton variant="low-emphasis" className="h-full w-full" />;
  }
  if (status === 'too_large' && isOfficeRenderer(renderer)) {
    return (
      <DownloadOnlyPanel
        message={`This ${extension} file is too large to preview.`}
        onDownload={download}
      />
    );
  }
  if (status === 'error' || status === 'too_large') {
    return (
      <div className="h-full flex items-center justify-center text-sm text-foreground-muted">
        {status === 'error'
          ? 'Failed to load file'
          : 'File too large to preview'}
      </div>
    );
  }
  if (renderer === 'canvas') {
    return (
      <CanvasFrame
        title={`Canvas ${displayName}`}
        chatId={chatId}
        canvasText={text}
        cacheKey={versionKey}
        parseResult={canvas}
        documentMode={canvasDocumentMode}
        onDocumentReadyChange={onCanvasDocumentReadyChange}
        className={cn(
          'w-full border-0',
          canvasDocumentMode ? 'block bg-transparent' : 'h-full bg-background'
        )}
      />
    );
  }
  if (renderer === 'html') {
    if (htmlViewMode === 'code' && userStore.isInternalUser) {
      return <PlainTextView text={text} />;
    }
    return (
      <SpriteFrame
        title={`Preview ${displayName}`}
        html={text}
        className="h-full w-full border-0 bg-white"
      />
    );
  }
  if (renderer === 'image') {
    return (
      <div className="h-full w-full flex items-center justify-center overflow-auto bg-surface p-4">
        <img
          src={url}
          alt={displayName}
          className="max-h-full max-w-full object-contain"
        />
      </div>
    );
  }
  if (renderer === 'pdf') {
    return (
      <PdfPreviewFrame
        title={`Preview ${displayName}`}
        url={url}
        hideToolbar
        className="h-full w-full border-0 bg-white"
      />
    );
  }
  if (isOfficeRenderer(renderer)) {
    if (!bytes) {
      return <Skeleton variant="low-emphasis" className="h-full w-full" />;
    }
    if (renderer === 'xlsx') {
      return <XlsxPreview bytes={bytes} onError={handleOfficeRenderError} />;
    }
    return (
      <OfficeFrame
        kind={renderer}
        bytes={bytes}
        onError={handleOfficeRenderError}
      />
    );
  }
  if (renderer === 'markdown') {
    return (
      <div className="h-full overflow-auto p-4 bg-surface select-text">
        <Markdown
          id={`md-preview-${file.id}`}
          content={text}
          className="text-sm text-foreground uniform-text-size"
        />
      </div>
    );
  }
  return <PlainTextView text={plainText} />;
}

const VersionedFilePreviewContent = observer(FilePreviewContentVersion);

export const FilePreviewContent = observer(function FilePreviewContent(
  props: FilePreviewContentProps
) {
  return (
    <VersionedFilePreviewContent
      key={getFileVersionKey(props.file)}
      {...props}
    />
  );
});
